import type { SegmentUsage, Usage } from '@customerio/hydra-ui-api';
import type { Segment } from './entity';
import type { SegmentsList } from './queries';
import { isEmptyString } from '@/utils/string';

/**
 * Pure segment derivations ported from the Ember `segment` model getters.
 * State/type predicates operate on an already-parsed `Segment`; the `usedIn*`
 * predicates operate on a `SegmentUsage` blob (fetched separately — the list
 * read passes `usage_info: 0`, so `Segment.usage` is absent there and usage is
 * hydrated via the usage endpoints; see `queries.ts`).
 */

/** Segment type filter values (Ember `SEGMENT_TYPES`): `default` = "all" (no filter). */
export const SEGMENT_TYPES = {
  default: 'all',
  manual: 'static',
  dynamic: 'dynamic',
  temporary: 'temporary',
  hidden: 'hidden',
} as const;

/* ----- type ----- */
export function isManual(segment: Pick<Segment, 'type'>): boolean {
  return segment.type === 'static';
}
export function isDynamic(segment: Pick<Segment, 'type'>): boolean {
  return segment.type === 'dynamic';
}
export function isTemporary(segment: Pick<Segment, 'type'>): boolean {
  return segment.type === 'temporary';
}
/** Whether the segment counts against the data-driven segment cap; a sample segment or a form's segment does not. */
export function countsAsDataDrivenSegment(segment: Pick<Segment, 'type' | 'sample' | 'linked_form_id'>): boolean {
  return segment.type === 'dynamic' && !segment.sample && isEmptyString(segment.linked_form_id);
}
export function segmentIcon(segment: Pick<Segment, 'type'>): string {
  return isManual(segment) ? 'segment-manual' : 'segment-data-driven';
}

/* ----- state ----- */
// `isBuilding` is "not finished" (covers the `build` state Ember never names explicitly).
export function isFinished(segment: Pick<Segment, 'state'>): boolean {
  return segment.state === 'finished';
}
export function isBuilding(segment: Pick<Segment, 'state'>): boolean {
  return segment.state !== 'finished';
}
export function isPreProcessing(segment: Pick<Segment, 'state'>): boolean {
  return segment.state === 'events_queued' || segment.state === 'events' || segment.state === 'build_queued';
}
export function hasProgress(segment: Pick<Segment, 'progress'>): boolean {
  return segment.progress != null && segment.progress !== 0;
}

/* ----- usage ----- */

// Segment-membership usages that are "active" — Ember drops Temporary/Hidden segments.
export function getActiveSegmentUsage(segments: readonly Usage[] | undefined): Usage[] {
  return (segments ?? []).filter((usage) => usage.type !== 'Temporary' && usage.type !== 'Hidden');
}

export function usedInActiveCampaigns(usage: SegmentUsage | undefined): boolean {
  return (usage?.campaigns ?? []).some((campaign) => !campaign.archived);
}

// Ember synthesizes `newsletters = sent + draft`; used-in-any is that combined length.
export function usedInAnyNewsletters(usage: SegmentUsage | undefined): boolean {
  return (usage?.sent_newsletters?.length ?? 0) + (usage?.draft_newsletters?.length ?? 0) > 0;
}
export function usedInUnsentNewsletters(usage: SegmentUsage | undefined): boolean {
  return (usage?.draft_newsletters?.length ?? 0) > 0;
}
export function usedInApiTriggeredBroadcasts(usage: SegmentUsage | undefined): boolean {
  return (usage?.broadcasts?.length ?? 0) > 0;
}
export function usedInSqlImports(usage: SegmentUsage | undefined): boolean {
  return (usage?.sql_imports?.length ?? 0) > 0;
}
export function usedInDashboards(usage: SegmentUsage | undefined): boolean {
  return (usage?.dashboard?.length ?? 0) > 0;
}
export function usedInActiveSegments(usage: SegmentUsage | undefined): boolean {
  return getActiveSegmentUsage(usage?.segments).length > 0;
}

/**
 * Whether the segment is referenced by anything that should block deletion.
 * `inUseArchived` is the segment's own `in_use_archived` flag (campaign usage
 * the usage blob doesn't itemize).
 */
export function usedByAnything(usage: SegmentUsage | undefined, inUseArchived: boolean): boolean {
  return (
    usedInActiveCampaigns(usage) ||
    inUseArchived ||
    usedInAnyNewsletters(usage) ||
    usedInApiTriggeredBroadcasts(usage) ||
    usedInSqlImports(usage) ||
    usedInDashboards(usage) ||
    usedInActiveSegments(usage)
  );
}

/* ----- form link ----- */

/**
 * Whether a form the segment belongs to still exists. The usage read names the form while it does,
 * and the server refuses to delete the segment then. Before usage loads, the segment's own link
 * stands in.
 */
export function isLinkedToAnyNonDeletedForm(
  segment: Pick<Segment, 'linked_form_id'>,
  usage: SegmentUsage | undefined,
): boolean {
  if (usage == null) return !isEmptyString(segment.linked_form_id);
  return usage.linked_form != null;
}

/** Whether the segment's form is connected, which the server refuses an archive for. */
export function isLinkedToConnectedForm(
  segment: Pick<Segment, 'linked_form_id'>,
  usage: SegmentUsage | undefined,
): boolean {
  if (usage == null) return !isEmptyString(segment.linked_form_id);
  return usage.linked_form?.connected === true;
}

/* ----- archive / delete ----- */

/** Campaigns count here whether or not they are archived; the usage read lists only the active ones. */
export function usedInAnyCampaigns(usage: SegmentUsage | undefined, inUseArchived: boolean): boolean {
  return usedInActiveCampaigns(usage) || inUseArchived;
}

/** Whether nothing that is still running depends on the segment, so the server lets it be archived. */
export function canBeArchived(
  segment: Pick<Segment, 'archived' | 'linked_form_id'>,
  usage: SegmentUsage | undefined,
): boolean {
  return (
    !segment.archived &&
    !usedInActiveCampaigns(usage) &&
    !usedInUnsentNewsletters(usage) &&
    !usedInApiTriggeredBroadcasts(usage) &&
    !isLinkedToConnectedForm(segment, usage) &&
    !usedInSqlImports(usage) &&
    !usedInActiveSegments(usage)
  );
}

/** Whether nothing references the segment, archived campaigns included, so the server lets it be deleted. */
export function canBeDeleted(
  segment: Pick<Segment, 'in_use_archived' | 'linked_form_id'>,
  usage: SegmentUsage | undefined,
): boolean {
  return (
    !usedInAnyCampaigns(usage, segment.in_use_archived) &&
    !usedInUnsentNewsletters(usage) &&
    !usedInApiTriggeredBroadcasts(usage) &&
    !isLinkedToAnyNonDeletedForm(segment, usage) &&
    !usedInSqlImports(usage) &&
    !usedInActiveSegments(usage)
  );
}

/* ----- list ----- */

/**
 * Return segments that are not tagged "Sample".
 *
 * Every new workspace gets 7–9 sample segments (e.g. "All Users",
 * "Paying Customers") which inflate segment counts and skew the
 * prompt card heuristics. The segments list carries a `tags` array
 * mapping tag IDs to names, and each segment has `tag_ids`.
 */
export function getNonSampleSegments(data: SegmentsList): SegmentsList['segments'] {
  const sampleTag = data.tags.find((t) => t.name === 'Sample');
  if (sampleTag == null) {
    return data.segments;
  }
  // `Tag.id` is a number while `Segment.tag_ids` is a string array on the wire,
  // so compare as strings.
  const sampleTagId = String(sampleTag.id);
  return data.segments.filter((s) => {
    const tags = s.tag_ids ?? [];
    return !tags.includes(sampleTagId);
  });
}
